import Vue from 'vue'
import { getLanguage } from '@/api/auth'
import { getAppUiText } from '@/utils/language/common'

export const MAX_DOWNLOAD_BATCH_COUNT = 10
export const MAX_RUNNING_DOWNLOAD_COUNT = 10

const state = Vue.observable({
  tasks: [],
  visible: false,
  collapsed: false
})

function getUi(language) {
  return getAppUiText(language || getLanguage())
}

function formatLanguageText(template, values = {}) {
  return String(template || '').replace(/\{(\w+)\}/g, (match, key) => (
    values[key] === undefined ? match : values[key]
  ))
}

function getRunningCount() {
  return state.tasks.filter(task => task && task.state == 'running').length
}

function getSummary(language) {
  const ui = getUi(language)
  const total = state.tasks.length
  const running = getRunningCount()
  const failed = state.tasks.filter(task => task && task.state == 'error').length
  if (running > 0) {
    return `${running}/${total} ${ui.downloading}`
  }
  if (failed > 0) {
    return `${failed}/${total} ${ui.downloadFailed}`
  }
  return `${total}/${total} ${ui.downloadDone}`
}

function validateStart(selectionCount, taskCount = selectionCount, language) {
  const ui = getUi(language)
  const selectedNumber = Number(selectionCount) || 0
  const taskNumber = Number(taskCount) || 0
  if (selectedNumber > MAX_DOWNLOAD_BATCH_COUNT) {
    return {
      ok: false,
      message: formatLanguageText(ui.maxBatchDownload, { count: MAX_DOWNLOAD_BATCH_COUNT })
    }
  }
  if (getRunningCount() + Math.max(taskNumber, 1) > MAX_RUNNING_DOWNLOAD_COUNT) {
    return {
      ok: false,
      message: formatLanguageText(ui.maxRunningDownload, { count: MAX_RUNNING_DOWNLOAD_COUNT })
    }
  }
  return { ok: true, message: '' }
}

function registerTaskHandlers(task, handlers = {}) {
  if (!task) {
    return task
  }
  Object.defineProperty(task, '__downloadHandlers', {
    value: Object.assign({}, handlers),
    configurable: true,
    enumerable: false,
    writable: true
  })
  return task
}

function addTask(task, handlers = {}) {
  if (!task) {
    return task
  }
  registerTaskHandlers(task, handlers)
  state.tasks.unshift(task)
  state.visible = true
  state.collapsed = false
  return task
}

function revokeTaskUrl(task) {
  if (task && task.openUrl && String(task.openUrl).startsWith('blob:') && typeof URL !== 'undefined') {
    URL.revokeObjectURL(task.openUrl)
  }
  if (task) {
    Vue.set(task, 'openUrl', '')
  }
}

function cancelTask(task) {
  if (!task) {
    return
  }
  Vue.set(task, 'cancelled', true)
  if (task.abortController && typeof task.abortController.abort == 'function') {
    task.abortController.abort()
  }
  const handlers = task.__downloadHandlers || {}
  if (typeof handlers.revoke == 'function') {
    handlers.revoke(task)
  } else {
    revokeTaskUrl(task)
  }
}

function removeTask(task) {
  if (!task) {
    return
  }
  cancelTask(task)
  state.tasks = state.tasks.filter(item => item && item.id != task.id)
  if (state.tasks.length == 0) {
    state.visible = false
    state.collapsed = false
  }
}

function closeList() {
  state.tasks.forEach(task => cancelTask(task))
  state.tasks = []
  state.visible = false
  state.collapsed = false
}

function retryTask(task) {
  if (!task || task.state != 'error') {
    return
  }
  const handlers = task.__downloadHandlers || {}
  if (typeof handlers.retry != 'function') {
    return
  }
  state.visible = true
  Vue.set(task, 'cancelled', false)
  Vue.set(task, 'abortController', null)
  Vue.set(task, 'state', 'running')
  Vue.set(task, 'statusText', getUi().continueDownload)
  handlers.retry(task)
}

function getNativeOpener() {
  if (typeof window === 'undefined') {
    return null
  }
  return window.viwoodsNative || window.electronAPI || window.nativeBridge || null
}

function canOpenTask(task) {
  if (!task || task.state != 'done') {
    return false
  }
  const nativeOpener = getNativeOpener()
  if (task.filePath && nativeOpener && typeof nativeOpener.showItemInFolder == 'function') {
    return true
  }
  return !!(nativeOpener && typeof nativeOpener.openDownloadFolder == 'function')
}

function openTask(task) {
  if (!canOpenTask(task)) {
    return
  }
  const nativeOpener = getNativeOpener()
  if (task.filePath && nativeOpener && typeof nativeOpener.showItemInFolder == 'function') {
    nativeOpener.showItemInFolder(task.filePath)
    return
  }
  if (nativeOpener && typeof nativeOpener.openDownloadFolder == 'function') {
    nativeOpener.openDownloadFolder()
  }
}

export default {
  state,
  getRunningCount,
  getSummary,
  validateStart,
  registerTaskHandlers,
  addTask,
  removeTask,
  closeList,
  retryTask,
  canOpenTask,
  openTask,
  revokeTaskUrl
}
